import React, { useEffect, useState } from 'react'
import { mark } from '../data/record'

// One footer, used by every page. It was duplicated inline in Main.jsx and
// Scroller.jsx, which is why the YouTube icon had to be added twice last time.
// Swap those blocks for <SiteFooter /> and any future change lands everywhere
// at once.

// Two explicit rows. On a phone these stack three then two, both centred; on
// a desktop the second row simply sits under the first. Letting five items
// wrap on their own scattered them.
// One flat list. Nesting the links in two containers meant the hairline rule
// between siblings never applied across the join, so the separator between the
// third and fourth was always missing. The break below splits them into two
// rows on a phone without breaking the sequence.
// The Girl and Her Piano: every page of the main site.
const gapLinks = [
  { label: 'Watch Film', href: '/#watch' },
  { label: 'Screenings', href: '/screenings' },
  { label: 'Be Aware', href: '/climate-awareness' },
  { label: 'Climate Resilience', href: '/climate-resilience' },
  { label: 'The Pianist', href: '/hillary-osullivan' },
  { label: 'Links', href: '/links' },
  { label: 'Contact', href: '/#contact' },
]

// Hands Like Ours: the film page, the chapters and the diary.
const hloLinks = [
  { label: 'Hands Like Ours', href: '/handslikeours' },
  { label: 'Chapters', href: '/handslikeours/chapters' },
  { label: 'Producer\u2019s diary', href: '/handslikeours/producers-diary' },
]


// The accounts, by name, then as icons underneath.
const socialLinks = [
  { label: 'Instagram', href: 'https://www.instagram.com/thegirlandherpiano' },
  { label: 'Facebook', href: 'https://www.facebook.com/thegirlandherpiano' },
  { label: 'YouTube', href: 'https://www.youtube.com/@hillaryosullivan' },
  { label: 'IMDb', href: 'https://www.imdb.com/title/tt37798371/' },
  { label: 'Email', href: 'mailto:info@thegirlandherpiano.com' },
]

const SocialIcons = () => (
  <div className='social-icons'>
    <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Instagram' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <rect x='2' y='2' width='20' height='20' rx='5' ry='5'></rect>
        <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'></path>
        <line x1='17.5' y1='6.5' x2='17.51' y2='6.5'></line>
      </svg>
    </a>
    <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Facebook' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' strokeWidth='0.5' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'></path>
      </svg>
    </a>
    <a href='https://www.youtube.com/@hillaryosullivan' target='_blank' rel='noopener noreferrer' aria-label='YouTube' className='social-icon social-icon--youtube'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
        <path d='M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z'></path>
      </svg>
    </a>
    <a href='https://www.imdb.com/title/tt37798371/' target='_blank' rel='noopener noreferrer' aria-label='IMDb' className='social-icon social-icon--imdb'>IMDb</a>
    <a href='mailto:info@thegirlandherpiano.com' aria-label='Email' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'></path>
        <polyline points='22,6 12,13 2,6'></polyline>
      </svg>
    </a>
  </div>
)

// Each film's section carries its own mark: the kira for The Girl and Her
// Piano, the hand for Hands Like Ours. 
const SiteFooter = ({ film }) => {
  // the page you are on, marked in the footer (read after load, so the
  // server copy and the first paint match)
  const [here, setHere] = useState('')
  useEffect(() => { setHere(window.location.pathname.replace(/\/+$/, '') || '/') }, [])
  const cls = (href) => (here && href === here ? 'is-here' : undefined)
  return (
  <footer className='footer'>
    <div className='content-container footer-inner'>
      {/* Three columns: the two films, the page's own film first, then the
          social accounts. On a phone they stack. */}
      <div className='footer-films'>
        {(film === 'hlo' ? ['hlo', 'gap'] : ['gap', 'hlo']).map((f) => (
          f === 'hlo' ? (
            <section className='footer-film' key='hlo' aria-label='Hands Like Ours'>
              <a className='footer-film-head' href='/handslikeours'>
                <img className='footer-film-mark footer-film-mark--hlo' src='/assets/hlo/hands-like-ours-mark.png' alt='' aria-hidden='true' />
                <span className='footer-film-name'>Hands Like Ours</span>
              </a>
              <p className='footer-film-sub'>A new documentary, filming 2027</p>
              <ul className='footer-film-links'>
                {hloLinks.map((l) => <li key={l.href}><a href={l.href} className={cls(l.href)} aria-current={cls(l.href) ? 'page' : undefined}>{l.label}</a></li>)}
              </ul>
            </section>
          ) : (
            <section className='footer-film' key='gap' aria-label='The Girl and Her Piano'>
              <a className='footer-film-head' href='/'>
                <img className='footer-film-mark footer-film-mark--gap' src={mark} alt='' aria-hidden='true' />
                <span className='footer-film-name'>The Girl and Her Piano</span>
              </a>
              <p className='footer-film-sub'>Short documentary, Bhutan, 2024</p>
              <ul className='footer-film-links'>
                {gapLinks.map((l) => <li key={l.href}><a href={l.href} className={cls(l.href)} aria-current={cls(l.href) ? 'page' : undefined}>{l.label}</a></li>)}
              </ul>
            </section>
          )
        ))}
        <section className='footer-film footer-follow' aria-label='Follow'>
          <p className='footer-film-head footer-follow-head'><span className='footer-film-name'>Follow</span></p>
          <ul className='footer-film-links'>
            {socialLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href} {...(l.href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}>{l.label}</a>
              </li>
            ))}
          </ul>
          <SocialIcons />
        </section>
      </div>

      <p className='footer-legal'>© 2026 White Room Studio Pte Ltd. All rights reserved.</p>
    </div>
  </footer>
)
}

export default SiteFooter
